<template>
  <a-modal
    :title="title"
    width="80%"
    :open="open"
    :footer="null"
    centered
    destroy-on-close
    :mask-closable="false"
    :body-style="{ maxHeight: '72vh', overflowY: 'auto' }"
    @cancel="handleCancel"
  >
    <a-spin
      :spinning="loading"
      tip="AI 正在根据 SQL 生成 Model..."
    >
      <div style="min-height: 420px">
        <a-alert
          v-if="!success"
          :message="message"
          type="error"
          show-icon
          style="margin-bottom: 16px"
        />

        <a-form
          layout="vertical"
          size="small"
        >
          <a-form-item label="查询 SQL（用于生成 Model）">
            <a-textarea
              v-model:value="sqlInput"
              placeholder="请输入一条 SELECT 查询 SQL，例如：SELECT id, name, created_at FROM my_table"
              :auto-size="{ minRows: 4, maxRows: 12 }"
              :disabled="loading"
              allow-clear
            />
          </a-form-item>
        </a-form>

        <div style="margin-bottom: 12px; display: flex; gap: 12px; align-items: center">
          <a-button
            type="primary"
            :loading="loading"
            :disabled="!sqlInput || !dataSourceId"
            @click="run_task"
          >
            生成模型
          </a-button>
          <a-button
            :disabled="loading"
            @click="handleCancel"
          >
            关闭
          </a-button>
          <span
            v-if="!dataSourceId"
            style="color: #999; font-size: 12px"
          >
            未获取到数据源信息，无法生成
          </span>
        </div>

        <a-divider
          orientation="left"
          v-if="markdonwResult"
        >
          生成结果
        </a-divider>
        <div
          class="mark-container"
          ref="markContainer"
        >
          <div v-html="htmlResult"></div>
        </div>
      </div>
    </a-spin>
  </a-modal>
</template>

<script setup lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue';
import { message as Message } from 'ant-design-vue';
import { getApiData } from 'liyu-pc-base/api/api-model';
import MarkdownIt from 'markdown-it';

interface Props {
  title: string;
  record: any;
}
const props = defineProps<Props>();
const emit = defineEmits(['cancel', 'ok']);

const open = ref(true);
const loading = ref(false);
const success = ref(true);
const message = ref('');
const sqlInput = ref('');
const markdonwResult = ref('');
const task_id = ref<any>(null);
const md = new MarkdownIt();

// 为代码块（fence）包裹容器并注入「复制」按钮，方便用户一键复制生成的 Model 代码
const defaultFenceRenderer = md.renderer.rules.fence;
md.renderer.rules.fence = (tokens, idx, options, env, self) => {
  const rendered =
    defaultFenceRenderer
      ? defaultFenceRenderer(tokens, idx, options, env, self)
      : self.renderToken(tokens, idx, options);
  const copyBtn =
    '<button class="md-copy-btn" type="button" aria-label="复制代码">' +
    '<span class="md-copy-icon">复制</span></button>';
  return `<div class="mark-code-block">${copyBtn}${rendered}</div>`;
};

let timer: any = null;

// 渲染容器引用（事件委托挂载点，代码块为 v-html 动态生成，故用委托统一处理复制）
const markContainer = ref<HTMLElement | null>(null);

const onContainerClick = async (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  const btn = target.closest('.md-copy-btn') as HTMLElement | null;
  if (!btn) return;
  const block = btn.closest('.mark-code-block');
  const codeEl = block?.querySelector('pre code');
  if (!codeEl) return;
  const text = (codeEl.textContent || '').replace(/\n$/, '');
  const ok = await copyToClipboard(text);
  if (ok) Message.success('已复制到剪贴板');
  else Message.error('复制失败，请手动复制');
  const label = btn.querySelector('.md-copy-icon');
  if (label) label.textContent = '已复制';
  setTimeout(() => {
    if (label) label.textContent = '复制';
  }, 1500);
};

// 优先使用 Clipboard API，失败时回退 execCommand
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallthrough */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

// 数据源 ID（来自列表行 record.id）
const dataSourceId = computed(() => props.record?.id ?? null);
const modelApp = computed(() => props.record?.model_app ?? '');

const htmlResult = computed(() => md.render(markdonwResult.value));

const clearTimer = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};

const run_task = async () => {
  if (!dataSourceId.value || !sqlInput.value) return;
  clearTimer();
  loading.value = true;
  success.value = true;
  message.value = '';
  markdonwResult.value = '';
  try {
    const res: any = await getApiData(
      {
        data_source_id: dataSourceId.value,
        sql: sqlInput.value,
      },
      `${modelApp.value}/ai_gen_model`,
    );
    task_id.value = res.task_id;
    get_task();
  } catch (e: any) {
    loading.value = false;
    success.value = false;
    message.value = e?.message || '发起任务时发生错误';
  }
};

const get_task = async () => {
  if (!task_id.value) return;
  try {
    const res: any = await getApiData(
      {
        task_id: task_id.value,
      },
      `${modelApp.value}/get_ai_gen_model_result`,
    );
    if (res.ready) {
      loading.value = false;
      success.value = res.success;
      if (res.success) {
        markdonwResult.value = res.result;
      } else {
        message.value = res.message || '获取结果失败';
      }
    } else {
      // 任务尚未完成，继续轮询
      success.value = true;
      message.value = '';
      markdonwResult.value = '';
      timer = setTimeout(get_task, 1000);
    }
  } catch (e: any) {
    loading.value = false;
    success.value = false;
    message.value = e?.message || '获取结果时发生错误';
  }
};

const handleCancel = () => {
  clearTimer();
  emit('cancel');
};

onMounted(() => {
  markContainer.value?.addEventListener('click', onContainerClick);
});

onUnmounted(() => {
  clearTimer();
  markContainer.value?.removeEventListener('click', onContainerClick);
});
</script>
<style lang="less">
/* 优雅的 Markdown 渲染样式 - 专为 .mark-container 设计 */
.mark-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.6;
  color: #2d3748;
  background-color: #ffffff;
  margin: 0 auto;
  padding: 1rem 0;
  font-size: 16px;
  overflow-wrap: break-word;
  word-break: break-word;
  box-sizing: border-box;
}

.mark-container h1,
.mark-container h2,
.mark-container h3,
.mark-container h4,
.mark-container h5,
.mark-container h6 {
  margin-top: 2.5rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  position: relative;
  padding-bottom: 0.5rem;
}

.mark-container h1 {
  font-size: 2.25rem;
  color: #4299e1;
}

.mark-container h2 {
  font-size: 1.75rem;
  border-bottom: 2px solid #e2e8f0;
}

.mark-container h3 {
  font-size: 1.5rem;
  color: #4299e1;
}

.mark-container h4 {
  font-size: 1.25rem;
  color: #4299e1;
}

.mark-container h5,
.mark-container h6 {
  font-size: 1rem;
  color: #4299e1;
}

.mark-container p {
  margin-bottom: 1.25rem;
  color: #4a5568;
}

.mark-container ul,
.mark-container ol {
  margin-bottom: 1.25rem;
  padding-left: 2rem;
}

.mark-container li {
  margin-bottom: 0.5rem;
}

.mark-container li > p {
  margin-bottom: 0.5rem;
}

/* 代码块 */
.mark-container pre {
  background-color: #f0f4f8;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin: 1.5rem 0;
  overflow-x: auto;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.95rem;
  line-height: 1.5;
}

.mark-container pre code {
  background-color: transparent;
  padding: 0;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
}

/* 引用块 */
.mark-container blockquote {
  border-left: 0.25rem solid #4299e1;
  padding: 1rem 1.5rem;
  margin: 1.5rem 0;
  background-color: rgba(66, 153, 225, 0.05);
  border-radius: 0 0.5rem 0.5rem 0;
  color: #4a5568;
}

/* 表格 */
.mark-container table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
}

.mark-container th,
.mark-container td {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  text-align: left;
}

.mark-container th {
  background-color: #f8fafc;
  font-weight: 600;
}

/* 链接 */
.mark-container a {
  color: #4299e1;
  text-decoration: none;
}

.mark-container a:hover {
  text-decoration: underline;
  color: #2b6cb0;
}

/* 分隔线 */
.mark-container hr {
  border: 0;
  border-top: 1px solid #e2e8f0;
  margin: 2rem 0;
}

/* 代码内联 */
.mark-container code {
  background-color: #f0f4f8;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.95rem;
  color: #2d3748;
}

/* 重点强调 */
.mark-container strong {
  color: #4299e1;
  font-weight: 600;
}

/* 响应式优化 */
@media (max-width: 768px) {
  .mark-container {
    padding: 1.5rem;
    font-size: 15px;
  }

  .mark-container h1 {
    font-size: 1.8rem;
  }

  .mark-container h2 {
    font-size: 1.4rem;
  }
}

/* 代码块复制按钮（随 fence 渲染注入） */
.mark-container .mark-code-block {
  position: relative;
}

.mark-container .md-copy-btn {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  z-index: 2;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #4a5568;
  background: #ffffff;
  border: 1px solid #cbd5e0;
  border-radius: 0.25rem;
  cursor: pointer;
  user-select: none;
}

.mark-container .md-copy-btn:hover {
  color: #4299e1;
  border-color: #4299e1;
}

.mark-container .md-copy-btn:active {
  background: #f0f4f8;
}
</style>
